import { message } from "antd";
import { getDownloadURL, listAll, ref } from "firebase/storage";
import { useState } from "react";
import { useDownloadURL } from "react-firebase-hooks/storage";
import { storage } from "../config/firebaseConfig";
import { AvatarImage, AvatarFallback, Avatar } from "./ui/avatar";
import { Mail, Phone, Loader2 } from "lucide-react";
import {
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
  Card,
} from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import MatchActionsDialog from "./match-actions-dialog";
import DocumentButtons from "./document-buttons";
import { ReviewStatus } from "@/types/topLevelReciprocalMatch";

const getReviewStatusBadge = (reviewStatus?: ReviewStatus) => {
  switch (reviewStatus) {
    case "marked_for_review":
      return (
        <Badge className="bg-orange-100 text-orange-800 border-orange-200">
          För granskning
        </Badge>
      );
    case "not_for_review":
      return (
        <Badge className="bg-gray-100 text-gray-800 border-gray-200">
          Inte för granskning
        </Badge>
      );
    case "pending":
      return (
        <Badge className="bg-blue-100 text-blue-800 border-blue-200">
          Väntande
        </Badge>
      );
    default:
      return null;
  }
};

export const getUnknownFileNames = async (
  personDataUid: string,
  typeOfFile: string
) => {
  try {
    const folderRef = ref(
      storage,
      `mobileUsersFiles/${personDataUid}/${typeOfFile}/`
    );

    listAll(folderRef)
      .then((res) => {
        if (res.items.length === 0) {
          message.error("Användaren har inte laddat upp något CV");
          return;
        }

        res.items.forEach((itemRef) => {
          getDownloadURL(ref(storage, itemRef.toString()))
            .then((url) => {
              let a = document.createElement("a");
              a.href = url;
              a.target = "_blank";
              a.download = "*.*";
              a.click();
            })
            .catch((e) => {
              console.error(e);
              alert("An error occured while fetching user data");
            });

          return itemRef;
        });
      })
      .catch((error) => {
        console.error(error);
        alert("An error occured while fetching user data");
      });
  } catch (error) {
    console.error(error);
    alert("An error occured while fetching user data");
  }
};

export default function MatchCard({
  candidate,
  selectedAdId,
  setSelectedMatch,
  refreshUsers,
}: {
  candidate: any;
  selectedAdId: string;
  setSelectedMatch: (match: any) => void;
  refreshUsers: React.Dispatch<React.SetStateAction<number>>;
}) {
  // firebase.storage().ref().getDownloadURL()
  /*   const [value loading, error] = useDownloadURL(
    ref(storage, `mobileUsersFiles/${candidate?.uid}/profile_image`)
  ); */
  const [showMoreTags, setShowMoreTags] = useState(false);
  /*   const [pdfUrl, setPdfUrl] = useState<string | null>(null); */

  const shouldLoadImage = Boolean(
    candidate.realFirebaseUserData.hasProfilePic ||
      candidate.realFirebaseUserData.profilePictureUrl
  );

  const [profileImageUrl, isLoadingProfileImage, profileImageError] =
    useDownloadURL(
      shouldLoadImage
        ? ref(storage, `mobileUsersFiles/${candidate?.uid}/profile_image`)
        : null
    );

  let name =
    candidate.realFirebaseUserData.givenName ||
    candidate.realFirebaseUserData.personName;
  let firstName = name?.split(" ")[0];
  let surname = candidate.realFirebaseUserData.surname;

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle>Läs mer om kandidaten</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center space-x-4 mb-4">
          <Avatar className="w-16 h-16">
            {isLoadingProfileImage ? (
              <AvatarFallback>
                <Loader2 className="h-4 w-4 animate-spin" />
              </AvatarFallback>
            ) : profileImageError ? (
              <AvatarFallback className="bg-primary/10 text-primary">
                <span className="text-xl font-medium">
                  {firstName?.[0]}
                  {surname?.[0]}
                </span>
              </AvatarFallback>
            ) : profileImageUrl ? (
              <AvatarImage
                className="object-cover"
                src={profileImageUrl}
                alt={`${firstName}'s avatar`}
              />
            ) : (
              <AvatarFallback className="bg-primary/10 text-primary">
                <span className="text-xl font-medium">
                  {firstName?.[0]}
                  {surname?.[0]}
                </span>
              </AvatarFallback>
            )}
          </Avatar>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xl font-semibold mb-0">
                {`${candidate.personName}, ${candidate.age} år`}
              </h2>
              {getReviewStatusBadge(candidate.reviewStatus)}
            </div>
            {candidate.currentJobTitle && candidate.currentCompany && (
              <p className="text-sm text-muted-foreground mb-0">
                {`${candidate.currentJobTitle?.trim()}, ${candidate.currentCompany?.trim()}`}
              </p>
            )}
          </div>
        </div>
        <p className="text-sm mb-4">{candidate.contentText}</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {candidate.tags && candidate.tags.length > 0 && (
            <>
              <div className="flex flex-wrap gap-2">
                {(showMoreTags
                  ? candidate.tags
                  : candidate.tags.slice(0, 10)
                ).map((tag: string, index: number) => (
                  <Badge key={tag + index} className="h-6 flex items-center">
                    {tag}
                  </Badge>
                ))}
              </div>
              {candidate.tags.length > 10 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 flex items-center"
                  onClick={() => setShowMoreTags(!showMoreTags)}>
                  {showMoreTags
                    ? "Visa färre"
                    : `+${candidate.tags.length - 10} fler`}
                </Button>
              )}
            </>
          )}
        </div>
        <div className="space-y-2 mb-4">
          <DocumentButtons
            documents={candidate.personalDocuments}
            candidateUid={candidate.uid}
          />
        </div>
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Kontaktinformation</h3>
          <div className="flex items-center space-x-2 text-sm">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <span>{candidate?.realFirebaseUserData.email}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <span>
              {candidate?.realFirebaseUserData.phoneNumber ||
                "(OBS! Användaren har inte lagt in något telefonnummer)"}
            </span>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <MatchActionsDialog
          candidate={candidate}
          selectedAdId={selectedAdId}
          setSelectedMatch={setSelectedMatch}
          refreshUsers={refreshUsers}
        />
      </CardFooter>
    </Card>
  );
}
